import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./VideoComputer.css";
import { useActionExecutor } from "./ActionExecutor.js";

// Resolve backend /uploads/ URLs to full origin so video plays when frontend is on different port (e.g. 3000 vs 8080)
function resolveVideoSrc(url) {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("blob:") || url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/uploads/")) {
    const base = (process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "") || "http://localhost:8080";
    return base + url;
  }
  return url;
}

export default function VideoComputer({ isOpen = false, onClose }) {
  const [videoFile, setVideoFile] = useState(null);
  const [musicFile, setMusicFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [clips, setClips] = useState([]); // [{ file, url, start, end, speed }]
  const [prompt, setPrompt] = useState("");
  const [editedVideoUrl, setEditedVideoUrl] = useState(null);
  const [lastCommands, setLastCommands] = useState([]);
  const [lastProject, setLastProject] = useState(null);
  const [verification, setVerification] = useState(null);
  const [verificationFix, setVerificationFix] = useState(null);
  const [toast, setToast] = useState(null);
  const [approvalMode, setApprovalMode] = useState(true);
  const [allowedTypes, setAllowedTypes] = useState(["uploadVideo", "uploadMusic", "editVideo", "download"]);
  const [history, setHistory] = useState([]); // [{ ts, prompt, originalUrl, editedUrl }]
  const [timelineIndex, setTimelineIndex] = useState(0);
  const [branches, setBranches] = useState([]); // [{ id, name, outputUrl, project, commands, verification }]
  const [compareBranchId, setCompareBranchId] = useState(null);
  const [musicVolume, setMusicVolume] = useState(0.35);
  const [videoVolume, setVideoVolume] = useState(1.0);
  const [startAt, setStartAt] = useState(0);
  const [muteOriginal, setMuteOriginal] = useState(false);
  const [musicFadeIn, setMusicFadeIn] = useState(0);
  const [musicFadeOut, setMusicFadeOut] = useState(0);
  const [duckingEnabled, setDuckingEnabled] = useState(false);
  const [duckingThresholdDb, setDuckingThresholdDb] = useState(-18);
  const [duckingRatio, setDuckingRatio] = useState(6);
  const [eqEnabled, setEqEnabled] = useState(false);
  const [eqLowGainDb, setEqLowGainDb] = useState(0);
  const [eqMidGainDb, setEqMidGainDb] = useState(0);
  const [eqHighGainDb, setEqHighGainDb] = useState(0);
  const [transitionType, setTransitionType] = useState("fade");
  const [transitionDuration, setTransitionDuration] = useState(0.5);
  const [filterPreset, setFilterPreset] = useState("none");
  const [zoomEnabled, setZoomEnabled] = useState(false);
  const [zoomStart, setZoomStart] = useState(0);
  const [zoomEnd, setZoomEnd] = useState(3);
  const [zoomFrom, setZoomFrom] = useState(1.0);
  const [zoomTo, setZoomTo] = useState(1.18);
  const [textOverlays, setTextOverlays] = useState([]);
  const [overlayTextDraft, setOverlayTextDraft] = useState({
    text: "",
    start: 0,
    end: 3,
    x: 40,
    y: 40,
    animate: false,
    x2: 240,
    y2: 40,
    alpha: 1,
    alpha2: 1,
    size: 32,
    color: "white",
    box: true,
    boxcolor: "black@0.4",
    boxborderw: 12,
    font: "Arial",
  });
  const [titleTemplate, setTitleTemplate] = useState("none"); // none | titleCenter | lowerThird
  const [titleText, setTitleText] = useState("");
  const [titleSubtext, setTitleSubtext] = useState("");
  const [titleStart, setTitleStart] = useState(0);
  const [titleEnd, setTitleEnd] = useState(3);
  const [overlayImageFile, setOverlayImageFile] = useState(null);
  const [overlayImageUrl, setOverlayImageUrl] = useState("");
  const [imageLibrary, setImageLibrary] = useState([]);
  const [overlayImage, setOverlayImage] = useState({
    start: 0,
    end: 3,
    x: 40,
    y: 40,
    x2: 240,
    y2: 40,
    animate: false,
    opacityAnimate: false,
    alpha: 1,
    alpha2: 1,
    width: 240,
    height: -1,
  });
  const projectFileInputRef = useRef(null);

  const DEFAULT_SIZE = { width: 1100, height: 820 };
  const MIN_SIZE = { width: 640, height: 520 };
  const MAX_SIZE = { width: 1400, height: 980 };
  const [panelSize, setPanelSize] = useState(() => {
    try {
      const raw = localStorage.getItem("zolaid-video-computer-size");
      if (!raw) return DEFAULT_SIZE;
      const parsed = JSON.parse(raw);
      const w = Number(parsed?.width);
      const h = Number(parsed?.height);
      if (!Number.isFinite(w) || !Number.isFinite(h)) return DEFAULT_SIZE;
      return {
        width: Math.max(MIN_SIZE.width, Math.min(MAX_SIZE.width, w)),
        height: Math.max(MIN_SIZE.height, Math.min(MAX_SIZE.height, h)),
      };
    } catch (_) {
      return DEFAULT_SIZE;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("zolaid-video-computer-size", JSON.stringify(panelSize));
    } catch (_) {}
  }, [panelSize]);

  useEffect(() => {
    if (!isOpen) return;
    try {
      const raw = localStorage.getItem("zolaid-image-library");
      const parsed = raw ? JSON.parse(raw) : [];
      setImageLibrary(Array.isArray(parsed) ? parsed : []);
    } catch (_) {
      setImageLibrary([]);
    }
  }, [isOpen]);

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = panelSize.width;
    const startH = panelSize.height;

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const nextW = Math.max(MIN_SIZE.width, Math.min(MAX_SIZE.width, startW + dx));
      const nextH = Math.max(MIN_SIZE.height, Math.min(MAX_SIZE.height, startH + dy));
      setPanelSize({ width: nextW, height: nextH });
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const actions = useMemo(() => {
    const list = [];
    if (videoFile && videoUrl) list.push({ type: "uploadVideo", description: "Video uploaded", delay: 200 });
    if (musicFile) list.push({ type: "uploadMusic", description: "Music uploaded", delay: 200 });
    if (videoFile && prompt.trim()) list.push({ type: "editVideo", description: "Apply AI edit", delay: 200, prompt });
    if (editedVideoUrl) list.push({ type: "download", description: "Download edited video", delay: 200 });
    return list;
  }, [videoFile, videoUrl, musicFile, prompt, editedVideoUrl]);

  const currentTask = useMemo(() => {
    if (!videoFile) return "Upload a video";
    if (!musicFile) return "Optional: add background music";
    if (!prompt.trim()) return "Describe the edit";
    if (!editedVideoUrl) return "Ready to apply AI edit";
    return "Edited video ready";
  }, [videoFile, musicFile, prompt, editedVideoUrl]);

  const applyToast = (msg) => {
    setToast(msg);
    window.clearTimeout(applyToast.__t);
    applyToast.__t = window.setTimeout(() => setToast(null), 1800);
  };

  const performEdit = async () => {
    if (!videoFile || !prompt.trim()) return;
    try {
      applyToast("Editingâ€¦");
      const formData = new FormData();
      formData.append("video", videoFile);
      if (musicFile) formData.append("music", musicFile);
      formData.append("prompt", prompt);
      formData.append(
        "options",
        JSON.stringify({
          musicVolume,
          videoVolume,
          startAt,
          muteOriginal,
        })
      );
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/chat/edit-video` : "/chat/edit-video";
      const response = await axios.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data?.videoUrl) {
        setEditedVideoUrl(response.data.videoUrl);
        const entry = { ts: Date.now(), prompt, originalUrl: videoUrl, editedUrl: response.data.videoUrl };
        setHistory((prev) => [entry, ...prev].slice(0, 25));
        setTimelineIndex(0);
        applyToast("Edit complete");
      } else {
        applyToast(response.data?.message || "No edited video returned");
      }
    } catch (e) {
      console.error(e);
      applyToast("Edit failed");
    }
  };

  const renderProject = async () => {
    if (!clips.length) return;
    try {
      applyToast("Renderingâ€¦");
      const formData = new FormData();
      clips.forEach((c) => formData.append("clips", c.file));
      if (musicFile) formData.append("music", musicFile);
      if (overlayImageFile) formData.append("overlayImage", overlayImageFile);

      const project = {
        clips: clips.map((c, idx) => ({
          index: idx,
          start: c.start === "" ? null : Number(c.start),
          end: c.end === "" ? null : Number(c.end),
          speed: c.speed === "" ? 1 : Number(c.speed),
        })),
        transitions: Array.from({ length: Math.max(0, clips.length - 1) }).map(() => ({
          type: transitionType,
          duration: Number(transitionDuration) || 0.5,
        })),
        filters: { preset: filterPreset, zoom: { enabled: zoomEnabled, start: zoomStart, end: zoomEnd, from: zoomFrom, to: zoomTo } },
        overlays: {
          text: textOverlays,
          image: overlayImageFile || overlayImageUrl ? overlayImage : null,
          imageUrl: overlayImageUrl || null,
        },
        audio: {
          musicVolume,
          videoVolume,
          startAt,
          muteOriginal,
          musicFadeIn,
          musicFadeOut,
          duckingEnabled,
          duckingThresholdDb,
          duckingRatio,
          eqEnabled,
          eqLowGainDb,
          eqMidGainDb,
          eqHighGainDb,
        },
        format: "mp4",
      };
      formData.append("project", JSON.stringify(project));

      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/video/render` : "/video/render";
      const response = await axios.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data?.videoUrl) {
        setEditedVideoUrl(response.data.videoUrl);
        setLastCommands(Array.isArray(response.data?.commands) ? response.data.commands : []);
        setLastProject(response.data?.project || project);
        setVerification(response.data?.verification || null);
        setVerificationFix(response.data?.verificationFix || null);

        // If backend created a fallback branch, persist it and auto-compare
        if (response.data?.fallbackBranch?.videoUrl) {
          const fb = response.data.fallbackBranch;
          const id = `fallback-${Date.now()}`;
          const name = "Fallback (safe)";
          const branch = {
            id,
            name,
            outputUrl: fb.videoUrl,
            project: fb.project || null,
            commands: Array.isArray(fb.commands) ? fb.commands : [],
            verification: fb.verification || null,
            verificationFix: fb.verificationFix || null,
          };
          setBranches((prev) => [branch, ...prev].slice(0, 25));
          setCompareBranchId(id);
          applyToast("Fallback branch created (compare on)");
        }

        const entry = { ts: Date.now(), prompt: "Render project", originalUrl: clips[0].url, editedUrl: response.data.videoUrl };
        setHistory((prev) => [entry, ...prev].slice(0, 25));
        setTimelineIndex(0);
        applyToast("Render complete");
      } else {
        applyToast(response.data?.message || "No rendered video returned");
      }
    } catch (e) {
      console.error(e);
      applyToast("Render failed");
    }
  };

  const onActionComplete = (action) => {
    if (action.type === "editVideo") performEdit();
    if (action.type === "download" && editedVideoUrl) {
      const a = document.createElement("a");
      a.href = editedVideoUrl;
      a.download = "edited-video.mp4";
      document.body.appendChild(a);
      a.click();
      a.remove();
      applyToast("Download started");
    }
  };

  const { currentAction, executedActions, awaitingApproval, approveNext, skipNext, currentActionIndex, reset } =
    useActionExecutor(actions, onActionComplete, {
      startIndex: 0,
      requireApproval: approvalMode,
      allowedActionTypes: allowedTypes,
    });

  const selectedVersion = history[timelineIndex] || null;
  const compareBranch = branches.find((b) => b.id === compareBranchId) || null;

  const downloadTextFile = (filename, content, mime = "text/plain;charset=utf-8") => {
    const blob = new Blob([content || ""], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exportCurrentProjectJson = () => {
    const project = {
      clips: clips.map((c, idx) => ({
        index: idx,
        start: c.start === "" ? null : Number(c.start),
        end: c.end === "" ? null : Number(c.end),
        speed: c.speed === "" ? 1 : Number(c.speed),
      })),
      transitions: Array.from({ length: Math.max(0, clips.length - 1) }).map(() => ({
        type: transitionType,
        duration: Number(transitionDuration) || 0.5,
      })),
      filters: { preset: filterPreset, zoom: { enabled: zoomEnabled, start: zoomStart, end: zoomEnd, from: zoomFrom, to: zoomTo } },
      overlays: {
        text: textOverlays,
        image: overlayImageFile ? overlayImage : null,
      },
      audio: {
        musicVolume,
        videoVolume,
        startAt,
        muteOriginal,
      },
      format: "mp4",
    };
    downloadTextFile("zolaid-video-project.json", JSON.stringify(project, null, 2), "application/json;charset=utf-8");
  };

  const importProjectJson = async (file) => {
    try {
      if (!file) return;
      const text = await file.text();
      const p = JSON.parse(text);
      if (!p || typeof p !== "object") throw new Error("Invalid JSON");

      // Apply audio
      if (p.audio) {
        if (Number.isFinite(Number(p.audio.musicVolume))) setMusicVolume(Number(p.audio.musicVolume));
        if (Number.isFinite(Number(p.audio.videoVolume))) setVideoVolume(Number(p.audio.videoVolume));
        if (Number.isFinite(Number(p.audio.startAt))) setStartAt(Math.max(0, Number(p.audio.startAt)));
        setMuteOriginal(Boolean(p.audio.muteOriginal));
      }

      // Apply transitions/filters
      const t0 = Array.isArray(p.transitions) ? p.transitions[0] : null;
      if (t0 && typeof t0 === "object") {
        if (typeof t0.type === "string") setTransitionType(t0.type);
        if (Number.isFinite(Number(t0.duration))) setTransitionDuration(Number(t0.duration));
      }
      if (p.filters && typeof p.filters.preset === "string") setFilterPreset(p.filters.preset);
      if (p.filters && typeof p.filters.zoom === "object") {
        setZoomEnabled(Boolean(p.filters.zoom.enabled));
        if (Number.isFinite(Number(p.filters.zoom.start))) setZoomStart(Math.max(0, Number(p.filters.zoom.start)));
        if (Number.isFinite(Number(p.filters.zoom.end))) setZoomEnd(Math.max(0, Number(p.filters.zoom.end)));
        if (Number.isFinite(Number(p.filters.zoom.from))) setZoomFrom(Math.max(1, Math.min(3, Number(p.filters.zoom.from))));
        if (Number.isFinite(Number(p.filters.zoom.to))) setZoomTo(Math.max(1, Math.min(3, Number(p.filters.zoom.to))));
      }

      // Overlays
      if (p.overlays && typeof p.overlays === "object") {
        if (Array.isArray(p.overlays.text)) setTextOverlays(p.overlays.text.slice(0, 15));
        if (p.overlays.image && typeof p.overlays.image === "object") setOverlayImage((prev) => ({ ...prev, ...p.overlays.image }));
      }

      // Clip trims/speeds (requires clips already uploaded)
      if (Array.isArray(p.clips) && clips.length > 0) {
        setClips((prev) =>
          prev.map((c, idx) => {
            const found = p.clips.find((x) => Number(x.index) === idx) || {};
            return {
              ...c,
              start: found.start == null ? "" : String(found.start),
              end: found.end == null ? "" : String(found.end),
              speed: found.speed == null ? c.speed : String(found.speed),
            };
          })
        );
      }

      applyToast("Project JSON loaded");
    } catch (e) {
      console.error(e);
      applyToast("Failed to load project JSON");
    } finally {
      if (projectFileInputRef.current) projectFileInputRef.current.value = "";
    }
  };

  // Persist UI state locally (does not persist media files)
  useEffect(() => {
    try {
      const snapshot = {
        transitionType,
        transitionDuration,
        filterPreset,
        zoomEnabled,
        zoomStart,
        zoomEnd,
        zoomFrom,
        zoomTo,
        textOverlays,
        overlayImage,
        musicVolume,
        videoVolume,
        startAt,
        muteOriginal,
      };
      localStorage.setItem("zolaid-video-project-ui", JSON.stringify(snapshot));
    } catch (_) {}
  }, [
    transitionType,
    transitionDuration,
    filterPreset,
    zoomEnabled,
    zoomStart,
    zoomEnd,
    zoomFrom,
    zoomTo,
    textOverlays,
    overlayImage,
    musicVolume,
    videoVolume,
    startAt,
    muteOriginal,
  ]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("zolaid-video-project-ui");
      if (!raw) return;
      const s = JSON.parse(raw);
      if (!s || typeof s !== "object") return;
      if (typeof s.transitionType === "string") setTransitionType(s.transitionType);
      if (Number.isFinite(Number(s.transitionDuration))) setTransitionDuration(Number(s.transitionDuration));
      if (typeof s.filterPreset === "string") setFilterPreset(s.filterPreset);
      if (typeof s.zoomEnabled === "boolean") setZoomEnabled(s.zoomEnabled);
      if (Number.isFinite(Number(s.zoomStart))) setZoomStart(Number(s.zoomStart));
      if (Number.isFinite(Number(s.zoomEnd))) setZoomEnd(Number(s.zoomEnd));
      if (Number.isFinite(Number(s.zoomFrom))) setZoomFrom(Number(s.zoomFrom));
      if (Number.isFinite(Number(s.zoomTo))) setZoomTo(Number(s.zoomTo));
      if (Array.isArray(s.textOverlays)) setTextOverlays(s.textOverlays.slice(0, 15));
      if (s.overlayImage && typeof s.overlayImage === "object") setOverlayImage((prev) => ({ ...prev, ...s.overlayImage }));
      if (Number.isFinite(Number(s.musicVolume))) setMusicVolume(Number(s.musicVolume));
      if (Number.isFinite(Number(s.videoVolume))) setVideoVolume(Number(s.videoVolume));
      if (Number.isFinite(Number(s.startAt))) setStartAt(Number(s.startAt));
      if (typeof s.muteOriginal === "boolean") setMuteOriginal(s.muteOriginal);
    } catch (_) {}
    // only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportViaBackend = async ({ kind, format }) => {
    if (!editedVideoUrl) return;
    try {
      applyToast("Exportingâ€¦");
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const endpoint = apiUrl ? `${apiUrl}/video/export` : "/video/export";
      const res = await axios.post(endpoint, { videoUrl: editedVideoUrl, kind, format });
      const url = kind === "audio" ? res.data?.audioUrl : res.data?.videoUrl;
      if (url) {
        const a = document.createElement("a");
        a.href = url;
        a.download = "";
        document.body.appendChild(a);
        a.click();
        a.remove();
        applyToast("Export ready");
      } else {
        applyToast("Export failed");
      }
    } catch (e) {
      console.error(e);
      applyToast("Export failed");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="video-computer-modal">
      <div
        className="video-computer"
        style={{
          width: `${panelSize.width}px`,
          height: `${panelSize.height}px`,
          maxWidth: `${MAX_SIZE.width}px`,
          maxHeight: `${MAX_SIZE.height}px`,
        }}
      >
        {toast && <div className="video-toast">{toast}</div>}
        <div className="video-resize-corner" onMouseDown={startResize} title="Resize"></div>

        <div className="video-header">
          <div className="video-title">ðŸŽ¬ Zolaid Video Computer</div>
          <button className="video-close" onClick={onClose} type="button">
            âœ•
          </button>
        </div>

        <div className="video-permissions">
          <label className="video-toggle">
            <input type="checkbox" checked={approvalMode} onChange={(e) => setApprovalMode(e.target.checked)} />
            Require approval
          </label>
          <div className="video-chips">
            {["uploadVideo", "uploadMusic", "editVideo", "download"].map((t) => (
              <button
                key={t}
                type="button"
                className={`video-chip ${allowedTypes.includes(t) ? "on" : "off"}`}
                onClick={() => setAllowedTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]))}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {awaitingApproval && currentAction && (
          <div className="video-approval">
            <div className="video-approval-left">
              <div className="video-approval-title">Approval required</div>
              <div className="video-approval-desc">{currentAction.description || currentAction.type}</div>
            </div>
            <div className="video-approval-actions">
              <button type="button" className="video-btn" onClick={approveNext}>
                Approve
              </button>
              <button type="button" className="video-btn" onClick={skipNext}>
                Skip
              </button>
              <button type="button" className="video-btn" onClick={reset}>
                Reset
              </button>
            </div>
          </div>
        )}

        <div className="video-status">
          <div className="video-status-left">Task: {currentTask}</div>
          <div className="video-status-right">
            Step {Math.min(currentActionIndex + 1, actions.length)}/{actions.length || 0}
          </div>
        </div>

        <div className="video-body">
          <div className="video-panel">
            <div className="video-panel-title">Inputs</div>
            <div className="video-form">
              <label className="video-label">Upload video</label>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setVideoFile(f);
                  setMusicFile(null);
                  setEditedVideoUrl(null);
                  setHistory([]);
                  setTimelineIndex(0);
                  if (f) setVideoUrl(URL.createObjectURL(f));
                }}
              />

              <label className="video-label">Project clips (multi-clip)</label>
              <input
                type="file"
                accept="video/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  const next = files.map((f) => ({
                    file: f,
                    url: URL.createObjectURL(f),
                    start: "",
                    end: "",
                    speed: "1",
                  }));
                  setClips(next);
                  setEditedVideoUrl(null);
                  setHistory([]);
                  setTimelineIndex(0);
                  if (files.length) applyToast(`${files.length} clips ready`);
                }}
              />

              {clips.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div
                    style={{
                      background: "rgba(0,0,0,0.03)",
                      border: "1px solid rgba(0,0,0,0.08)",
                      borderRadius: "10px",
                      padding: "8px",
                    }}
                  >
                    <div style={{ fontSize: "12px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      Transitions + Filters
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginTop: "8px" }}>
                      <label className="video-label">
                        Transition
                        <select
                          value={transitionType}
                          onChange={(e) => setTransitionType(e.target.value)}
                          style={{ width: "100%", padding: "8px", borderRadius: "10px", border: "1px solid rgba(0,0,0,0.12)" }}
                        >
                          <option value="none">None (hard cut)</option>
                          <option value="fade">Fade</option>
                          <option value="dissolve">Dissolve</option>
                          <option value="wipeleft">Wipe Left</option>
                          <option value="wiperight">Wipe Right</option>
                          <option value="slideleft">Slide Left</option>
                          <option value="slideright">Slide Right</option>
                        </select>
                      </label>
                      <label className="video-label">
                        Duration (s)
                        <input
                          type="number"
                          min="0"
                          max="3"
                          step="0.1"
                          value={transitionDuration}
                          onChange={(e) => setTransitionDuration(parseFloat(e.target.value || "0.5"))}
                          style={{ width: "100%" }}
                        />
                      </label>
                      <label className="video-label">
                        Filter preset
                        <select
                          value={filterPreset}
                          onChange={(e) => setFilterPreset(e.target.value)}
                          style={{ width: "100%", padding: "8px", borderRadius: "10px", border: "1px solid rgba(0,0,0,0.12)" }}
                        >
                          <option value="none">None</option>
                          <option value="grayscale">Grayscale</option>
                          <option value="cinematic">Cinematic</option>
                          <option value="blur">Blur</option>
                          <option value="punchin">Punch-in (Zoom)</option>
                        </select>
                      </label>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginTop: "8px" }}>
                      <label className="video-label" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <input type="checkbox" checked={zoomEnabled} onChange={(e) => setZoomEnabled(e.target.checked)} />
                        Zoom keyframes (global)
                      </label>
                      <label className="video-label">
                        Zoom start (s)
                        <input type="number" min="0" step="0.1" value={zoomStart} onChange={(e) => setZoomStart(parseFloat(e.target.value || "0"))} style={{ width: "100%" }} />
                      </label>
                      <label className="video-label">
                        Zoom end (s)
                        <input type="number" min="0" step="0.1" value={zoomEnd} onChange={(e) => setZoomEnd(parseFloat(e.target.value || "3"))} style={{ width: "100%" }} />
                      </label>
                      <label className="video-label">
                        Zoom from
                        <input type="number" min="1" max="3" step="0.01" value={zoomFrom} onChange={(e) => setZoomFrom(parseFloat(e.target.value || "1"))} style={{ width: "100%" }} disabled={!zoomEnabled} />
                      </label>
                      <label className="video-label">
                        Zoom to
                        <input type="number" min="1" max="3" step="0.01" value={zoomTo} onChange={(e) => setZoomTo(parseFloat(e.target.value || "1.1"))} style={{ width: "100%" }} disabled={!zoomEnabled} />
                      </label>
                      <div style={{ fontSize: 12, color: "rgba(0,0,0,0.65)", alignSelf: "end" }}>
                        Tip: 1.00 â†’ 1.15 feels like a â€œpunch-inâ€.
                      </div>
                    </div>
                  </div>

                  {clips.map((c, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: "rgba(0,0,0,0.03)",
                        border: "1px solid rgba(0,0,0,0.08)",
                        borderRadius: "10px",
                        padding: "8px",
                      }}
                    >
                      <div style={{ fontSize: "12px", fontWeight: 700 }}>Clip {idx + 1}</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginTop: "6px" }}>
                        <label className="video-label">
                          Start (s)
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            value={c.start}
                            onChange={(e) =>
                              setClips((prev) =>
                                prev.map((x, i) => (i === idx ? { ...x, start: e.target.value } : x))
                              )
                            }
                            style={{ width: "100%" }}
                          />
                        </label>
                        <label className="video-label">
                          End (s)
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            value={c.end}
                            onChange={(e) =>
                              setClips((prev) =>
                                prev.map((x, i) => (i === idx ? { ...x, end: e.target.value } : x))
                              )
                            }
                            style={{ width: "100%" }}
                          />
                        </label>
                        <label className="video-label">
                          Speed
                          <input
                            type="number"
                            min="0.25"
                            max="4"
                            step="0.05"
                            value={c.speed}
                            onChange={(e) =>
                              setClips((prev) =>
                                prev.map((x, i) => (i === idx ? { ...x, speed: e.target.value } : x))
                              )
                            }
                            style={{ width: "100%" }}
                          />
                        </label>
                      </div>
                    </div>
                  ))}

                  <div
                    style={{
                      background: "rgba(0,0,0,0.03)",
                      border: "1px solid rgba(0,0,0,0.08)",
                      borderRadius: "10px",
                      padding: "8px",
                    }}
                  >
                    <div style={{ fontSize: "12px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      Overlays
                    </div>

                    <div style={{ marginTop: "8px" }}>
                      <div style={{ fontSize: "12px", fontWeight: 700, marginBottom: "6px" }}>Title templates</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                        <label className="video-label">
                          Template
                          <select
                            value={titleTemplate}
                            onChange={(e) => setTitleTemplate(e.target.value)}
                            style={{ width: "100%", padding: "8px", borderRadius: "10px", border: "1px solid rgba(0,0,0,0.12)" }}
                          >
                            <option value="none">None</option>
                            <option value="titleCenter">Center Title</option>
                            <option value="lowerThird">Lower Third</option>
                          </select>
                        </label>
                        <label className="video-label">
                          Title
                          <input
                            type="text"
                            value={titleText}
                            onChange={(e) => setTitleText(e.target.value)}
                            style={{ width: "100%" }}
                          />
                        </label>
                        <label className="video-label">
                          Subtitle (optional)
                          <input
                            type="text"
                            value={titleSubtext}
                            onChange={(e) => setTitleSubtext(e.target.value)}
                            style={{ width: "100%" }}
                          />
                        </label>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                          <label className="video-label">
                            Start (s)
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={titleStart}
                              onChange={(e) => setTitleStart(parseFloat(e.target.value || "0"))}
                              style={{ width: "100%" }}
                            />
                          </label>
                          <label className="video-label">
                            End (s)
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={titleEnd}
                              onChange={(e) => setTitleEnd(parseFloat(e.target.value || "3"))}
                              style={{ width: "100%" }}
                            />
                          </label>
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
                        <button
                          type="button"
                          className="video-btn"
                          onClick={() => {
                            if (titleTemplate === "none" || !titleText.trim()) return;
                            const start = titleStart;
                            const end = Math.max(titleStart, titleEnd);
                            const base = {
                              start,
                              end,
                              color: "white",
                              font: "Arial",
                              box: true,
                              boxcolor: "black@0.45",
                              boxborderw: 18,
                            };

                            if (titleTemplate === "titleCenter") {
                              const main = {
                                ...base,
                                text: titleText,
                                // approximate center
                                x: 180,
                                y: 260,
                                size: 56,
                              };
                              const sub = titleSubtext.trim()
                                ? {
                                    ...base,
                                    text: titleSubtext,
                                    x: 220,
                                    y: 340,
                                    size: 28,
                                    boxborderw: 14,
                                  }
                                : null;
                              setTextOverlays((prev) => [...prev, main, ...(sub ? [sub] : [])].slice(0, 15));
                              applyToast("Title template added");
                              return;
                            }

                            if (titleTemplate === "lowerThird") {
                              const main = {
                                ...base,
                                text: titleText,
                                x: 40,
                                y: 560,
                                size: 40,
                              };
                              const sub = titleSubtext.trim()
                                ? {
                                    ...base,
                                    text: titleSubtext,
                                    x: 40,
                                    y: 610,
                                    size: 24,
                                    boxborderw: 14,
                                  }
                                : null;
                              setTextOverlays((prev) => [...prev, main, ...(sub ? [sub] : [])].slice(0, 15));
                              applyToast("Lower third added");
                            }
                          }}
                        >
                          Add template
                        </button>
                        <button
                          type="button"
                          className="video-btn"
                          onClick={() => {
                            setTitleTemplate("none");
                            setTitleText("");
                            setTitleSubtext("");
                          }}
                        >
                          Clear title fields
                        </button>
                      </div>
                    </div>

                    <div style={{ marginTop: "8px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                      <label className="video-label">
                        Text
                        <input
                          type="text"
                          value={overlayTextDraft.text}
                          onChange={(e) => setOverlayTextDraft((p) => ({ ...p, text: e.target.value }))}
                          style={{ width: "100%" }}
                        />
                      </label>
                      <label className="video-label">
                        Color
                        <input
                          type="text"
                          value={overlayTextDraft.color}
                          onChange={(e) => setOverlayTextDraft((p) => ({ ...p, color: e.target.value }))}
                          style={{ width: "100%" }}
                        />
                      </label>
                      <label className="video-label" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <input type="checkbox" checked={Boolean(overlayTextDraft.animate)} onChange={(e) => setOverlayTextDraft((p) => ({ ...p, animate: e.target.checked }))} />
                        Animate (keyframes)
                      </label>
                      <label className="video-label">
                        Start (s)
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={overlayTextDraft.start}
                          onChange={(e) => setOverlayTextDraft((p) => ({ ...p, start: parseFloat(e.target.value || "0") }))}
                          style={{ width: "100%" }}
                        />
                      </label>
                      <label className="video-label">
                        End (s)
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={overlayTextDraft.end}
                          onChange={(e) => setOverlayTextDraft((p) => ({ ...p, end: parseFloat(e.target.value || "3") }))}
                          style={{ width: "100%" }}
                        />
                      </label>
                      <label className="video-label">
                        X
                        <input
                          type="number"
                          value={overlayTextDraft.x}
                          onChange={(e) => setOverlayTextDraft((p) => ({ ...p, x: parseFloat(e.target.value || "0") }))}
                          style={{ width: "100%" }}
                        />
                      </label>
                      <label className="video-label">
                        Y
                        <input
                          type="number"
                          value={overlayTextDraft.y}
                          onChange={(e) => setOverlayTextDraft((p) => ({ ...p, y: parseFloat(e.target.value || "0") }))}
                          style={{ width: "100%" }}
                        />
                      </label>
                      {overlayTextDraft.animate && (
                        <>
                          <label className="video-label">
                            X2 (end)
                            <input type="number" value={overlayTextDraft.x2} onChange={(e) => setOverlayTextDraft((p) => ({ ...p, x2: parseFloat(e.target.value || "0") }))} style={{ width: "100%" }} />
                          </label>
                          <label className="video-label">
                            Y2 (end)
                            <input type="number" value={overlayTextDraft.y2} onChange={(e) => setOverlayTextDraft((p) => ({ ...p, y2: parseFloat(e.target.value || "0") }))} style={{ width: "100%" }} />
                          </label>
                          <label className="video-label">
                            Opacity (start)
                            <input type="number" min="0" max="1" step="0.05" value={overlayTextDraft.alpha} onChange={(e) => setOverlayTextDraft((p) => ({ ...p, alpha: parseFloat(e.target.value || "1") }))} style={{ width: "100%" }} />
                          </label>
                          <label className="video-label">
                            Opacity (end)
                            <input type="number" min="0" max="1" step="0.05" value={overlayTextDraft.alpha2} onChange={(e) => setOverlayTextDraft((p) => ({ ...p, alpha2: parseFloat(e.target.value || "1") }))} style={{ width: "100%" }} />
                          </label>
                        </>
                      )}
                      <label className="video-label">
                        Size
                        <input
                          type="number"
                          min="10"
                          value={overlayTextDraft.size}
                          onChange={(e) => setOverlayTextDraft((p) => ({ ...p, size: parseFloat(e.target.value || "32") }))}
                          style={{ width: "100%" }}
                        />
                      </label>
                      <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
                        <button
                          type="button"
                          className="video-btn"
                          onClick={() => {
                            if (!overlayTextDraft.text.trim()) return;
                            setTextOverlays((prev) => [...prev, { ...overlayTextDraft }].slice(0, 15));
                            setOverlayTextDraft((p) => ({ ...p, text: "" }));
                            applyToast("Text overlay added");
                          }}
                        >
                          Add text
                        </button>
                        <button type="button" className="video-btn" onClick={() => setTextOverlays([])}>
                          Clear
                        </button>
                      </div>
                    </div>

                    {textOverlays.length > 0 && (
                      <div style={{ marginTop: "8px", fontSize: "12px" }}>
                        <strong>Text overlays:</strong> {textOverlays.length}
                      </div>
                    )}

                    <div style={{ marginTop: "10px" }}>
                      <label className="video-label">Image overlay (optional)</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const f = e.target.files?.[0] || null;
                          setOverlayImageFile(f);
                          setOverlayImageUrl("");
                          if (f) applyToast("Overlay image ready");
                        }}
                      />
                      {imageLibrary.length > 0 && (
                        <label className="video-label" style={{ marginTop: "8px" }}>
                          Or pick from ImageComputer library
                          <select
                            value={overlayImageUrl || ""}
                            onChange={(e) => {
                              const url = e.target.value || "";
                              setOverlayImageUrl(url);
                              if (url) setOverlayImageFile(null);
                            }}
                            style={{ width: "100%" }}
                          >
                            <option value="">None</option>
                            {imageLibrary.slice(0, 20).map((it) => (
                              <option key={it.url} value={it.url}>
                                {it.url.split("/").slice(-1)[0]}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}

                      {(overlayImageFile || overlayImageUrl) && (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginTop: "8px" }}>
                          <label className="video-label">
                            Start (s)
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={overlayImage.start}
                              onChange={(e) => setOverlayImage((p) => ({ ...p, start: parseFloat(e.target.value || "0") }))}
                              style={{ width: "100%" }}
                            />
                          </label>
                          <label className="video-label">
                            End (s)
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={overlayImage.end}
                              onChange={(e) => setOverlayImage((p) => ({ ...p, end: parseFloat(e.target.value || "3") }))}
                              style={{ width: "100%" }}
                            />
                          </label>
                          <label className="video-label" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                            <input type="checkbox" checked={Boolean(overlayImage.animate)} onChange={(e) => setOverlayImage((p) => ({ ...p, animate: e.target.checked }))} />
                            Animate (keyframes)
                          </label>
                          <label className="video-label" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                            <input
                              type="checkbox"
                              checked={Boolean(overlayImage.opacityAnimate)}
                              onChange={(e) => setOverlayImage((p) => ({ ...p, opacityAnimate: e.target.checked }))}
                            />
                            Fade (opacity keyframes)
                          </label>
                          <label className="video-label">
                            Width (px)
                            <input
                              type="number"
                              min="10"
                              value={overlayImage.width}
                              onChange={(e) => setOverlayImage((p) => ({ ...p, width: parseFloat(e.target.value || "240") }))}
                              style={{ width: "100%" }}
                            />
                          </label>
                          <label className="video-label">
                            Height (px, -1=auto)
                            <input
                              type="number"
                              value={overlayImage.height}
                              onChange={(e) => setOverlayImage((p) => ({ ...p, height: parseFloat(e.target.value || "-1") }))}
                              style={{ width: "100%" }}
                            />
                          </label>
                          <label className="video-label">
                            X
                            <input
                              type="number"
                              value={overlayImage.x}
                              onChange={(e) => setOverlayImage((p) => ({ ...p, x: parseFloat(e.target.value || "0") }))}
                              style={{ width: "100%" }}
                            />
                          </label>
                          <label className="video-label">
                            Y
                            <input
                              type="number"
                              value={overlayImage.y}
                              onChange={(e) => setOverlayImage((p) => ({ ...p, y: parseFloat(e.target.value || "0") }))}
                              style={{ width: "100%" }}
                            />
                          </label>
                          {overlayImage.animate && (
                            <>
                              <label className="video-label">
                                X2 (end)
                                <input type="number" value={overlayImage.x2} onChange={(e) => setOverlayImage((p) => ({ ...p, x2: parseFloat(e.target.value || "0") }))} style={{ width: "100%" }} />
                              </label>
                              <label className="video-label">
                                Y2 (end)
                                <input type="number" value={overlayImage.y2} onChange={(e) => setOverlayImage((p) => ({ ...p, y2: parseFloat(e.target.value || "0") }))} style={{ width: "100%" }} />
                              </label>
                            </>
                          )}
                          {overlayImage.opacityAnimate && (
                            <>
                              <label className="video-label">
                                Opacity (start)
                                <input
                                  type="number"
                                  min="0"
                                  max="1"
                                  step="0.05"
                                  value={overlayImage.alpha}
                                  onChange={(e) => setOverlayImage((p) => ({ ...p, alpha: parseFloat(e.target.value || "1") }))}
                                  style={{ width: "100%" }}
                                />
                              </label>
                              <label className="video-label">
                                Opacity (end)
                                <input
                                  type="number"
                                  min="0"
                                  max="1"
                                  step="0.05"
                                  value={overlayImage.alpha2}
                                  onChange={(e) => setOverlayImage((p) => ({ ...p, alpha2: parseFloat(e.target.value || "1") }))}
                                  style={{ width: "100%" }}
                                />
                              </label>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <button type="button" className="video-primary" onClick={renderProject}>
                    Render project (merge + transitions/overlays/filters)
                  </button>
                </div>
              )}

              <label className="video-label">Background music (optional)</label>
              <input
                type="file"
                accept="audio/*"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setMusicFile(f);
                  if (f) applyToast("Music ready");
                }}
              />

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                <label className="video-label">
                  Music vol
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.01"
                    value={musicVolume}
                    onChange={(e) => setMusicVolume(parseFloat(e.target.value))}
                    style={{ width: "100%" }}
                  />
                </label>
                <label className="video-label">
                  Video vol
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.01"
                    value={videoVolume}
                    onChange={(e) => setVideoVolume(parseFloat(e.target.value))}
                    style={{ width: "100%" }}
                  />
                </label>
              </div>

              <label className="video-label">
                Music start (sec)
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={startAt}
                  onChange={(e) => setStartAt(parseFloat(e.target.value || "0"))}
                  style={{ width: "100%" }}
                />
              </label>

              <label className="video-label" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <input type="checkbox" checked={muteOriginal} onChange={(e) => setMuteOriginal(e.target.checked)} />
                Mute original audio (replace with music)
              </label>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                <label className="video-label">
                  Music fade-in (sec)
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={musicFadeIn}
                    onChange={(e) => setMusicFadeIn(parseFloat(e.target.value || "0"))}
                    style={{ width: "100%" }}
                  />
                </label>
                <label className="video-label">
                  Music fade-out (sec)
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={musicFadeOut}
                    onChange={(e) => setMusicFadeOut(parseFloat(e.target.value || "0"))}
                    style={{ width: "100%" }}
                  />
                </label>
              </div>

              <label className="video-label" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <input type="checkbox" checked={duckingEnabled} onChange={(e) => setDuckingEnabled(e.target.checked)} />
                Duck music under voice (auto)
              </label>
              {duckingEnabled && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                  <label className="video-label">
                    Duck threshold (dB)
                    <input
                      type="number"
                      step="1"
                      value={duckingThresholdDb}
                      onChange={(e) => setDuckingThresholdDb(parseFloat(e.target.value || "-18"))}
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label className="video-label">
                    Duck ratio
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={duckingRatio}
                      onChange={(e) => setDuckingRatio(parseFloat(e.target.value || "6"))}
                      style={{ width: "100%" }}
                    />
                  </label>
                </div>
              )}

              <label className="video-label" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <input type="checkbox" checked={eqEnabled} onChange={(e) => setEqEnabled(e.target.checked)} />
                EQ (music)
              </label>
              {eqEnabled && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
                  <label className="video-label">
                    Low (dB)
                    <input
                      type="number"
                      step="0.5"
                      value={eqLowGainDb}
                      onChange={(e) => setEqLowGainDb(parseFloat(e.target.value || "0"))}
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label className="video-label">
                    Mid (dB)
                    <input
                      type="number"
                      step="0.5"
                      value={eqMidGainDb}
                      onChange={(e) => setEqMidGainDb(parseFloat(e.target.value || "0"))}
                      style={{ width: "100%" }}
                    />
                  </label>
                  <label className="video-label">
                    High (dB)
                    <input
                      type="number"
                      step="0.5"
                      value={eqHighGainDb}
                      onChange={(e) => setEqHighGainDb(parseFloat(e.target.value || "0"))}
                      style={{ width: "100%" }}
                    />
                  </label>
                </div>
              )}

              <label className="video-label">Edit prompt</label>
              <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} />

              <button
                type="button"
                className="video-primary"
                onClick={() => {
                  // Kick the executor forward; approvals will gate the actual step.
                  applyToast("Queued actions (approve to run)");
                }}
                disabled={!videoFile || !prompt.trim()}
              >
                Queue edit actions
              </button>
            </div>
          </div>

          <div className="video-panel">
            <div className="video-panel-title">Preview</div>
            <div className="video-preview">
              {videoUrl && (
                <>
                  <div className="video-preview-label">Original</div>
                  <video
                    key={resolveVideoSrc(selectedVersion?.originalUrl || videoUrl)}
                    src={resolveVideoSrc(selectedVersion?.originalUrl || videoUrl)}
                    controls
                    className="video-el"
                    preload="auto"
                    playsInline
                  />
                </>
              )}
              <div style={{ display: "grid", gridTemplateColumns: compareBranch?.outputUrl ? "1fr 1fr" : "1fr", gap: "10px" }}>
                {(selectedVersion?.editedUrl || editedVideoUrl) && (
                  <div>
                    <div className="video-preview-label">Edited (current)</div>
                    <video
                      src={resolveVideoSrc(selectedVersion?.editedUrl || editedVideoUrl)}
                      controls
                      className="video-el"
                      preload="metadata"
                      playsInline
                    />
                  </div>
                )}
                {compareBranch?.outputUrl && (
                  <div>
                    <div className="video-preview-label">Edited (branch: {compareBranch.name})</div>
                    <video
                      src={resolveVideoSrc(compareBranch.outputUrl)}
                      controls
                      className="video-el"
                      preload="metadata"
                      playsInline
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="video-panel">
            <div className="video-panel-title">Timeline</div>
            <div className="video-timeline">
              <div className="video-timeline-row">
                <div className="video-timeline-label">
                  Versions: {timelineIndex}/{Math.max(history.length - 1, 0)}
                </div>
                <input
                  type="range"
                  min={0}
                  max={Math.max(history.length - 1, 0)}
                  value={Math.min(timelineIndex, Math.max(history.length - 1, 0))}
                  onChange={(e) => setTimelineIndex(parseInt(e.target.value, 10))}
                />
              </div>
              {selectedVersion && (
                <div className="video-timeline-meta">
                  <div>
                    <strong>Prompt:</strong> {selectedVersion.prompt}
                  </div>
                  <div>
                    <strong>Time:</strong> {new Date(selectedVersion.ts).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="video-panel">
            <div className="video-panel-title">Execution Log</div>
            <div className="video-log">
              {(executedActions.length ? executedActions : actions).map((a, idx) => (
                <div key={idx} className="video-log-item">
                  <div className="video-log-type">{a.type}</div>
                  <div className="video-log-desc">{a.description || ""}</div>
                </div>
              ))}
              {!actions.length && <div className="video-log-empty">No actions yet</div>}
            </div>
          </div>

          <div className="video-panel">
            <div className="video-panel-title">Verification</div>
            {verification ? (
              <div style={{ fontSize: "12px", color: "rgba(0,0,0,0.75)" }}>
                <div>
                  <strong>Status:</strong> {verification.ok ? "âœ… OK" : "âŒ Issues"}
                </div>
                {verificationFix && (
                  <div>
                    <strong>Auto-fix:</strong> {verificationFix}
                  </div>
                )}
                {verification.duration != null && (
                  <div>
                    <strong>Duration:</strong> {verification.duration.toFixed(2)}s
                  </div>
                )}
                <div>
                  <strong>Audio:</strong> {verification.hasAudio ? "yes" : "no"} â€¢ <strong>Video:</strong>{" "}
                  {verification.hasVideo ? "yes" : "no"}
                </div>
                {Array.isArray(verification.checks) && (
                  <ul style={{ margin: "8px 0 0 18px" }}>
                    {verification.checks.map((c, idx) => (
                      <li key={idx}>
                        {c.ok ? "âœ…" : "âŒ"} {c.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className="video-log-empty">No verification report yet</div>
            )}
          </div>

          <div className="video-panel">
            <div className="video-panel-title">Branches & Export</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button type="button" className="video-btn" onClick={exportCurrentProjectJson}>
                  Save project JSON
                </button>
                <input
                  ref={projectFileInputRef}
                  type="file"
                  accept="application/json,.json"
                  style={{ display: "none" }}
                  onChange={(e) => importProjectJson(e.target.files?.[0] || null)}
                />
                <button type="button" className="video-btn" onClick={() => projectFileInputRef.current?.click()}>
                  Load project JSON
                </button>
              </div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="video-btn"
                  disabled={!editedVideoUrl}
                  onClick={() => {
                    if (!editedVideoUrl) return;
                    const id = Date.now().toString();
                    const name = `Branch ${branches.length + 1}`;
                    setBranches((prev) => [
                      { id, name, outputUrl: editedVideoUrl, project: lastProject, commands: lastCommands, verification },
                      ...prev,
                    ]);
                    applyToast("Saved branch");
                  }}
                >
                  Save as branch
                </button>
                <button
                  type="button"
                  className="video-btn"
                  disabled={!branches.length}
                  onClick={() => setCompareBranchId((prev) => (prev ? null : branches[0]?.id || null))}
                >
                  {compareBranchId ? "Stop compare" : "Compare"}
                </button>
              </div>

              {branches.length > 0 && (
                <label className="video-label">
                  Compare branch
                  <select
                    value={compareBranchId || ""}
                    onChange={(e) => setCompareBranchId(e.target.value || null)}
                    style={{ width: "100%", padding: "8px", borderRadius: "10px", border: "1px solid rgba(0,0,0,0.12)" }}
                  >
                    <option value="">None</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button type="button" className="video-btn" disabled={!editedVideoUrl} onClick={() => exportViaBackend({ kind: "video", format: "mp4" })}>
                  Export MP4
                </button>
                <button type="button" className="video-btn" disabled={!editedVideoUrl} onClick={() => exportViaBackend({ kind: "video", format: "webm" })}>
                  Export WEBM
                </button>
                <button type="button" className="video-btn" disabled={!editedVideoUrl} onClick={() => exportViaBackend({ kind: "video", format: "mov" })}>
                  Export MOV
                </button>
                <button type="button" className="video-btn" disabled={!editedVideoUrl} onClick={() => exportViaBackend({ kind: "audio", format: "mp3" })}>
                  Export MP3
                </button>
                <button type="button" className="video-btn" disabled={!editedVideoUrl} onClick={() => exportViaBackend({ kind: "audio", format: "wav" })}>
                  Export WAV
                </button>
              </div>

              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="video-btn"
                  disabled={!lastProject}
                  onClick={() => downloadTextFile("zolaid-video-project.json", JSON.stringify(lastProject, null, 2), "application/json;charset=utf-8")}
                >
                  Download project JSON
                </button>
                <button
                  type="button"
                  className="video-btn"
                  disabled={!lastCommands?.length}
                  onClick={() => downloadTextFile("zolaid-ffmpeg-commands.txt", String(lastCommands.join("\n\n")), "text/plain;charset=utf-8")}
                >
                  Download FFmpeg commands
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


